import { gridTotal, } from '@signa/shared';
import { panelCells } from './fit.js';
import { apply, computeHomography, DegenerateQuadError, invert, } from './homography.js';
import { toStage } from './layout.js';
/**
 * 曲面の枠の形(#361)。
 *
 * 正本は [ADR-0060](../../../docs/adr/0060-curved-surface.md) の決定 4(短冊)・決定 5(表裏と隠れ)・
 * 決定 6(写し方)・決定 7(座標系)。
 *
 * ## 何をするか
 *
 * **保存した形(`curve.shape`)を写すだけで、解かない**(決定 3 —— 解くのは S04 だけ)。
 * 展開図を縦の細い短冊に分け、**短冊 × 行の片**ごとに「展開図の部分矩形 → 写した 4 隅」のホモグラフィで描く
 * (折れた枠の面ごとの描画 —— [`folds.ts`](./folds.ts) —— の延長。**シェーダは変えない**)。
 *
 * - **切り口の点は格子に 1 度だけ写し、片の 4 隅はその配列を共有する** —— 隣の片と同じ値なので継ぎ目に割れが出ない
 *   ([ADR-0029](../../../docs/adr/0029-panel-split-grid.md) の決定 3・ADR-0057 の決定 2 と同じ理屈)
 * - **必ず切る位置**: 展開図の両端・セルの縦の境目・**表裏の境**。その間を角度 2° 以下に等分する。行の境目で上下にも切る
 * - 片の中は**弦の平面**で近似する(半径の 0.015% —— 決定 4)。縦の遠近は射影なので正確
 *
 * ## 座標系(決定 7)
 *
 * 写した点は `shape.aspect` の**正方形の座標**(キャンバスの高さを 1)で求め、**`x / shape.aspect` で正規化座標に戻す** ——
 * 背景を差し替えてキャンバスの縦横比が変わっても、写真と同じに伸びる。**今のキャンバスの縦横比は使わない。**
 *
 * **形は `quad` ではなく `shape` から出る**ので、平面・折れた枠の「座標系はどれでもよい」は成り立たない ——
 * 割り付けは**正規化座標**か、変換(`DisplayTransform`)を渡して**表示座標**で返す。
 *
 * **すべて純粋関数。** WebGL も DOM も触らないので検査できる([ADR-0019](../../../docs/adr/0019-dev-foundation.md))。
 */
/** 短冊の幅の上限。**角度で 2°**(ADR-0060 の決定 4)。 */
export const MAX_STRIP_ANGLE = Math.PI / 90;
/** 1 周(ラジアン)。 */
const FULL_TURN = 2 * Math.PI;
/**
 * **1 周とみなす幅の誤差。** 保存では両端を別々に小数第 6 位へ丸めるので、1 周の幅は 2π から最大 10⁻⁶ ずれる
 * (`±π` → `±3.141593` で幅 `6.283186` —— #360 の申し送り)。余裕を見て 10⁻⁵。
 */
const FULL_TURN_SLACK = 1e-5;
/**
 * **これより近い点(カメラ座標の z)は写さない。** カメラの面に近い点は写真の外のはるか遠くへ飛び、
 * 後ろ(z ≤ 0)の点は裏返って写る。単位は円柱の半径。
 */
const MIN_DEPTH = 1e-6;
/**
 * 切り口を同じ位置とみなす差(展開図の横の位置 u)。表裏の境がセルの境目に重なったとき、
 * 幅がほぼ 0 の短冊を作らない。
 */
const SAME_CUT = 1e-9;
function dot(a, b) {
    return a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
}
function cross(a, b) {
    return [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
}
function scaled(v, k) {
    return [v[0] * k, v[1] * k, v[2] * k];
}
/** 正規化。長さが 0・有限でなければ `null`。 */
function unit(v) {
    const length = Math.hypot(v[0], v[1], v[2]);
    return Number.isFinite(length) && length > 0 ? scaled(v, 1 / length) : null;
}
/** `[0, m)` に収める剰余。 */
function modulo(value, m) {
    return ((value % m) + m) % m;
}
/**
 * 保存した形から**写像**を作る。**描けない形なら `null`** —— 業務 API の検査(`curveProblems`)は形の正しさまでは見ない
 * (#360 の申し送り)ので、ここで弾く:
 *
 * - `axis` が 0・`ref` が `axis` と平行(直交化すると 0)
 * - 正の数であるべき値(`aspect`・`focal`・`height`)が正でない・角度の幅が 0・有限でない値
 * - **柱の一周(`column`)なのに角度の幅が 1 周に足りない**(業務 API は幅を強制しない —— 柱の一周は左右とも輪郭で端を持たないので、
 *   1 周でなければ盤面の端が決まらない)
 *
 * 盤面がカメラの後ろにかかる形は写像を作れるが、**写せない点を含む片を描かない**([`curvedLayout`](#curvedLayout))。
 *
 * ## 表裏(決定 5)
 *
 * 外向きの法線 `n(θ) = cos θ·ref' + sin θ·b'` は軸に直交するので、`n·P = n·center + 1` は**高さに依らない** ——
 * **表裏は角度だけで決まる。** カメラは原点にあるので、
 *
 * - **外向き(`column` / `convex`)**: 外側がカメラを向く —— `n·P < 0` が表
 * - **内向き(`concave`)**: 内側がカメラを向く —— `n·P > 0` が表
 *
 * `n·P = ρ cos(θ − φ) + 1`(ρ・φ は `center` の軸に直交する成分の長さと向き —— 軸からカメラまでの距離)なので、
 * **境は `cos(θ − φ) = −1/ρ` の閉じた式**で求まる。ρ ≤ 1(カメラが円柱の内側)なら境は無い。
 */
export function curveProjector(curve) {
    const { shape } = curve;
    const positive = [shape.aspect, shape.focal, shape.height];
    if (!positive.every((value) => Number.isFinite(value) && value > 0))
        return null;
    if (![...shape.center, ...shape.angles].every(Number.isFinite))
        return null;
    const axis = unit(shape.axis);
    if (axis === null)
        return null;
    // `ref` を軸に直交させる。**元の長さに対して小さすぎる残りは平行とみなす**(向きが決まらない)。
    const along = dot(shape.ref, axis);
    const rest = [
        shape.ref[0] - along * axis[0],
        shape.ref[1] - along * axis[1],
        shape.ref[2] - along * axis[2],
    ];
    const refLength = Math.hypot(shape.ref[0], shape.ref[1], shape.ref[2]);
    if (!(Math.hypot(rest[0], rest[1], rest[2]) > refLength * 1e-9))
        return null;
    const ref = unit(rest);
    if (ref === null)
        return null;
    const side = cross(axis, ref);
    const raw = shape.angles[1] - shape.angles[0];
    if (raw === 0 || !Number.isFinite(raw))
        return null;
    const fullTurn = Math.abs(raw) >= FULL_TURN - FULL_TURN_SLACK;
    if (curve.kind === 'column' && !fullTurn)
        return null;
    const span = Math.sign(raw) * (fullTurn ? FULL_TURN : Math.abs(raw));
    const start = shape.angles[0];
    // 表裏(上の説明)。`n·P = cr cos θ + cs sin θ + 1`。
    const cr = dot(shape.center, ref);
    const cs = dot(shape.center, side);
    const facing = (angle) => cr * Math.cos(angle) + cs * Math.sin(angle) + 1;
    const isFront = curve.kind === 'concave'
        ? (angle) => facing(angle) > 0
        : (angle) => facing(angle) < 0;
    const rho = Math.hypot(cr, cs);
    const boundaries = [];
    if (rho > 1) {
        const phi = Math.atan2(cs, cr);
        const half = Math.acos(-1 / rho);
        boundaries.push(phi - half, phi + half);
    }
    return {
        kind: curve.kind,
        shape,
        start,
        span,
        fullTurn,
        angleAt: (u) => start + u * span,
        point: (angle, height) => {
            const c = Math.cos(angle);
            const s = Math.sin(angle);
            return [
                shape.center[0] + c * ref[0] + s * side[0] + height * axis[0],
                shape.center[1] + c * ref[1] + s * side[1] + height * axis[1],
                shape.center[2] + c * ref[2] + s * side[2] + height * axis[2],
            ];
        },
        project: ([x, y, z]) => {
            if (!(z > MIN_DEPTH))
                return null;
            // 写真の点は「キャンバスの高さを 1」とした正方形の座標。**`shape.aspect` で正規化座標に戻す**(決定 7)。
            const px = shape.aspect / 2 + (shape.focal * x) / z;
            const py = 1 / 2 + (shape.focal * y) / z;
            const point = [px / shape.aspect, py];
            return point.every(Number.isFinite) ? point : null;
        },
        isFront,
        boundaries,
    };
}
/** 項の累積和を `total` で割った境目の位置(0 と 1 を除く)。 */
function innerEdges(terms, total) {
    const edges = [];
    let sum = 0;
    for (const term of terms.slice(0, -1)) {
        sum += term;
        edges.push(sum / total);
    }
    return edges;
}
/** 角度 `angle` が展開図のどこ(u)にあるか。**両端を除く範囲**に無ければ `null`。 */
function positionOf(projector, angle) {
    const width = Math.abs(projector.span);
    const offset = modulo(Math.sign(projector.span) * (angle - projector.start), FULL_TURN);
    return offset > 0 && offset < width ? offset / width : null;
}
/**
 * **横の切り口。** 展開図の両端(0 と 1)と、内側の必ず切る位置(`inner`)を並べ、その間を**角度 `MAX_STRIP_ANGLE` 以下**に等分する。
 *
 * 必ず切る位置は**同じ値のまま**残す(等分の点は区間ごとに求める)—— 見える範囲の端が短冊の幅で丸まらない。
 * **両端は必ず残す** —— 端にほぼ重なる内側の位置のほうを捨てる(展開図の端が 1 からずれると、1 周の継ぎ目が閉じない)。
 */
function stripCuts(inner, span) {
    const anchors = [0];
    for (const cut of [...inner].sort((a, b) => a - b)) {
        const last = anchors[anchors.length - 1] ?? 0;
        if (cut - last > SAME_CUT && cut < 1 - SAME_CUT)
            anchors.push(cut);
    }
    anchors.push(1);
    const cuts = [];
    anchors.forEach((left, i) => {
        cuts.push(left);
        const right = anchors[i + 1];
        if (right === undefined)
            return;
        const count = Math.max(1, Math.ceil(((right - left) * Math.abs(span)) / MAX_STRIP_ANGLE - 1e-9));
        for (let k = 1; k < count; k += 1)
            cuts.push(left + ((right - left) * k) / count);
    });
    return cuts;
}
/**
 * **曲面の枠の割り付け**(ADR-0060 の決定 4)。描けない形なら `null`([`curveProjector`](#curveProjector))。
 *
 * - 展開図の寸法は `grid` の合計(無ければ `回り込み角 : 高さ`)。u は角度に、v は高さに比例する(v = 0 が上端)
 * - **必ず切る位置**: 展開図の両端(1 周ならここが継ぎ目)・セルの縦の境目・表裏の境。その間を 2° 以下に等分する
 * - 縦は行の境目でだけ切る(縦の遠近は射影なので、短冊は上下に分けなくても正確)
 * - **片の 4 隅は切り口の格子の値そのもの** —— 隣の片と同じ値を共有する。写せない点を含む片は除く
 *
 * `transform` を渡すと写した点を**表示座標**にする(描画コアの内側 —— 表示と書き出し)。
 * 渡さなければ**正規化座標**(当たり判定・輪郭の公開の口 —— S04・ビューアは正規化座標で呼ぶ)。
 */
export function curvedLayout(curve, grid, transform) {
    const projector = curveProjector(curve);
    if (projector === null)
        return null;
    const total = grid == null ? null : gridTotal(grid);
    const panel = total === null
        ? { width: Math.abs(projector.span), height: projector.shape.height }
        : { width: total.width, height: total.height };
    if (!(panel.width > 0 && panel.height > 0))
        return null;
    const columnEdges = grid == null ? [] : innerEdges(grid.columns, panel.width);
    const boundaries = projector.boundaries.flatMap((angle) => {
        const at = positionOf(projector, angle);
        return at === null ? [] : [at];
    });
    const columns = stripCuts([...columnEdges, ...boundaries], projector.span);
    const rows = [0, ...(grid == null ? [] : innerEdges(grid.rows, panel.height)), 1];
    const place = (point) => transform === undefined ? point : toStage(point, transform);
    const points = columns.map((u) => {
        const angle = projector.angleAt(u);
        return rows.map((v) => {
            const projected = projector.project(projector.point(angle, projector.shape.height * (1 - v)));
            return projected === null ? null : place(projected);
        });
    });
    // **1 周なら継ぎ目を同じ値にする**(右端 = 左端の角度 + 2π)。別々に写すと浮動小数の差が出る。
    const first = points[0];
    if (projector.fullTurn && first !== undefined)
        points[points.length - 1] = first;
    const panelPoints = columns.map((u) => rows.map((v) => [u * panel.width, v * panel.height]));
    const strips = columns.length - 1;
    const fronts = [];
    const stripColumns = [];
    for (let i = 0; i < strips; i += 1) {
        const middle = ((columns[i] ?? 0) + (columns[i + 1] ?? 0)) / 2;
        fronts.push(projector.isFront(projector.angleAt(middle)));
        // セルの境目で必ず切っているので、短冊の中ほどで数えれば列が決まる。
        stripColumns.push(columnEdges.filter((edge) => edge < middle).length);
    }
    const gridColumns = grid?.columns.length ?? 1;
    const pieces = [];
    for (let j = 0; j + 1 < rows.length; j += 1) {
        for (let i = 0; i < strips; i += 1) {
            const corners = quadAt(points, i, j);
            const panelCorners = quadAt(panelPoints, i, j);
            if (corners === null || panelCorners === null)
                continue;
            pieces.push({
                strip: i,
                row: j,
                cellIndex: j * gridColumns + (stripColumns[i] ?? 0),
                front: fronts[i] ?? false,
                panelCorners,
                corners,
            });
        }
    }
    return {
        projector,
        panel,
        cells: panelCells(panel, grid),
        columns,
        rows,
        fronts,
        points,
        pieces,
    };
}
/** 格子の `(i, j)` を左上とする 4 隅(左上から時計回り)。欠けていれば `null`。 */
function quadAt(points, i, j) {
    const tl = points[i]?.[j];
    const tr = points[i + 1]?.[j];
    const br = points[i + 1]?.[j + 1];
    const bl = points[i]?.[j + 1];
    if (tl == null || tr == null || br == null || bl == null)
        return null;
    return [tl, tr, br, bl];
}
/**
 * **片のホモグラフィ**(展開図の部分矩形 → 写した 4 隅)の逆行列を付ける。
 *
 * **潰れた片は `null`**(輪郭のすぐ近くで幅が 0 になる片 —— 決定 4)。今の潰れたクアッドと同じく、エラーにはしない。
 */
export function pieceDraw(piece) {
    let inverse;
    try {
        inverse = invert(computeHomography(piece.panelCorners, piece.corners));
    }
    catch (error) {
        if (error instanceof DegenerateQuadError)
            return null;
        throw error;
    }
    return inverse.every(Number.isFinite) ? { ...piece, inverse } : null;
}
/**
 * 展開図の点の**カメラからの距離**(半径を 1 とする)。当たり判定で「いちばん近い片」を選ぶのに使う(決定 5)。
 */
export function depthAt(layout, [x, y]) {
    const { projector, panel } = layout;
    const angle = projector.angleAt(x / panel.width);
    const [px, py, pz] = projector.point(angle, projector.shape.height * (1 - y / panel.height));
    return Math.hypot(px, py, pz);
}
/**
 * **曲面の枠の描画計画**(決定 4・5)。表示([`webgl-renderer.ts`](./webgl-renderer.ts))と
 * 書き出し([`compose.ts`](./compose.ts))が同じものを使い、GL への流し方は [`curve-draw.ts`](./curve-draw.ts) に 1 つだけ置く。
 *
 * **内向きの裏の片は表の片より手前にある** —— 視線は円柱に入る点(外側が見える = 内向きの裏)の後で
 * 出る点(内側が見える = 内向きの表)に当たる。だから「裏の片をステンシルに書く → 表の片をその外に描く」で隠れが正しく出る。
 * 表の片どうし・裏の片どうしは重ならない(視線は円柱に 1 回ずつしか入らない・出ない)ので、順序の問題は残らない。
 */
export function curvedDrawPlan(layout) {
    const concave = layout.projector.kind === 'concave';
    const occluders = [];
    const byCell = new Map();
    for (const piece of layout.pieces) {
        // **使う片だけ逆行列を作る** —— 表の片と、内向きの裏の片。外向きの裏の片は描かない(片の数は数百〜千を超える)。
        if (!piece.front && !concave)
            continue;
        const draw = pieceDraw(piece);
        if (draw === null)
            continue;
        if (!draw.front) {
            occluders.push(draw);
            continue;
        }
        const pieces = byCell.get(draw.cellIndex);
        if (pieces === undefined)
            byCell.set(draw.cellIndex, [draw]);
        else
            pieces.push(draw);
    }
    const cells = layout.cells.flatMap((cell) => {
        const pieces = byCell.get(cell.index);
        return pieces === undefined ? [] : [{ cell, pieces }];
    });
    return { panel: layout.panel, occluders, cells };
}
/**
 * **曲面の輪郭**(決定 5)。`cellIndex` を渡せばそのセル、省略すれば枠全体。
 *
 * 片が欠けた短冊(カメラの後ろにかかる)は輪郭に入れない —— その前後で多角形が分かれる。
 */
export function curvedOutline(layout, cellIndex) {
    const front = runs(layout, (piece) => piece.front && (cellIndex === undefined || piece.cellIndex === cellIndex));
    const occluders = layout.projector.kind === 'concave' ? runs(layout, (piece) => !piece.front) : [];
    return { polygons: front, occluders };
}
/**
 * 条件に合う片を、**続いている短冊ごと**の多角形にする。
 *
 * 短冊が多角形に入るのは、**条件に合う片が、その範囲の行すべてにそろう**とき。範囲の行は条件に合う片の行の最小〜最大
 * (セル 1 つなら 1 行、枠全体なら全行)。
 */
function runs(layout, accept) {
    const accepted = layout.pieces.filter(accept);
    if (accepted.length === 0)
        return [];
    const top = Math.min(...accepted.map((piece) => piece.row));
    const bottom = Math.max(...accepted.map((piece) => piece.row)) + 1;
    const rowsByStrip = new Map();
    for (const piece of accepted) {
        const rows = rowsByStrip.get(piece.strip) ?? new Set();
        rows.add(piece.row);
        rowsByStrip.set(piece.strip, rows);
    }
    const strips = layout.columns.length - 1;
    const included = (strip) => (rowsByStrip.get(strip)?.size ?? 0) === bottom - top;
    const groups = [];
    for (let strip = 0; strip < strips; strip += 1) {
        if (!included(strip))
            continue;
        const current = groups[groups.length - 1];
        if (current?.[current.length - 1] === strip - 1)
            current.push(strip);
        else
            groups.push([strip]);
    }
    // **1 周なら継ぎ目をまたぐ 2 つをつなぐ**(右端の短冊の次は左端の短冊 —— 切り口の点は同じ値)。
    const first = groups[0];
    const last = groups[groups.length - 1];
    if (layout.projector.fullTurn &&
        groups.length > 1 &&
        first !== undefined &&
        last !== undefined &&
        first[0] === 0 &&
        last[last.length - 1] === strips - 1) {
        groups.pop();
        groups[0] = [...last, ...first];
    }
    return groups.map((group) => {
        // 短冊の左の切り口を順に並べ、最後に右の切り口を足す(右端の短冊の右は継ぎ目 —— 1 周なら左端と同じ点)。
        const cuts = [...group, (group[group.length - 1] ?? 0) + 1];
        const upper = cuts.map((i) => layout.points[i]?.[top]);
        const lower = cuts.map((i) => layout.points[i]?.[bottom]).reverse();
        return [...upper, ...lower].filter((point) => point != null);
    });
}
/** 多角形の面積(絶対値)。いちばん大きい輪郭を選ぶのに使う。 */
function areaOf(polygon) {
    let twice = 0;
    polygon.forEach(([x0, y0], i) => {
        const [x1, y1] = polygon[(i + 1) % polygon.length] ?? [x0, y0];
        twice += x0 * y1 - x1 * y0;
    });
    return Math.abs(twice) / 2;
}
/**
 * **いちばん大きい多角形。** 無ければ `null`。
 *
 * 1 つの多角形しか受けない口(ビューアの内外判定・ドロップ先の強調 —— `frameOutline` / `cellOutline`)に使う。
 * 見えている部分が 2 つに分かれるのは、回り込み角の大きい弧の両端だけが見える形に限られる。
 */
export function largestPolygon(polygons) {
    let best = null;
    let bestArea = -1;
    for (const polygon of polygons) {
        const area = areaOf(polygon);
        if (area > bestArea) {
            best = polygon;
            bestArea = area;
        }
    }
    return best;
}
/**
 * **曲面の当たり判定**(決定 5)。点を含む片のうち**カメラにいちばん近いもの**のセル番号。それが裏なら `null`
 * (内向きの手前の裏・外向きの縁の上に回り込んだ裏 —— 素材は描かれず、写真の実物が見えている)。どの片にも入らなければ `null`。
 *
 * `point` と `layout` は同じ座標系(正規化座標なら `curvedLayout` に変換を渡さない)。
 * 片の内外は射影で見る(アフィン変換で変わらない)・近さは 3D の距離で比べる —— **キャンバスの縦横比は使わない**。
 *
 * 縁には余裕を持たせる(平面の枠の `cellAt` と同じ —— 内包判定で「内側」と出た点が丸めでわずかに外へ出ても弾かない)。
 */
export function curvedCellAt(layout, point) {
    const tolerance = Math.max(layout.panel.width, layout.panel.height) * 1e-6;
    let nearest = null;
    for (const piece of layout.pieces) {
        if (!boxContains(piece.corners, point))
            continue;
        const draw = pieceDraw(piece);
        if (draw === null)
            continue;
        const [x, y] = apply(draw.inverse, point);
        if (!Number.isFinite(x) || !Number.isFinite(y))
            continue;
        const [left, top] = piece.panelCorners[0];
        const [right, bottom] = piece.panelCorners[2];
        if (x < left - tolerance || x > right + tolerance)
            continue;
        if (y < top - tolerance || y > bottom + tolerance)
            continue;
        const depth = depthAt(layout, [x, y]);
        if (nearest === null || depth < nearest.depth)
            nearest = { depth, piece };
    }
    return nearest?.piece.front === true ? nearest.piece.cellIndex : null;
}
/**
 * 4 隅の外接矩形に入るか(**逆行列を作る前の絞り込み**)。片の数は数百になるので、ほとんどをここで落とす。
 * 余裕は外接矩形の大きさの 10⁻⁶ —— 縁の点を落とさない。
 */
function boxContains(corners, [x, y]) {
    const xs = corners.map(([cx]) => cx);
    const ys = corners.map(([, cy]) => cy);
    const minX = Math.min(...xs);
    const maxX = Math.max(...xs);
    const minY = Math.min(...ys);
    const maxY = Math.max(...ys);
    const margin = Math.max(maxX - minX, maxY - minY) * 1e-6;
    return x >= minX - margin && x <= maxX + margin && y >= minY - margin && y <= maxY + margin;
}
//# sourceMappingURL=curve.js.map